/**
 * @fileoverview Context for managing student signup flow state
 * @description Provides shared state for single-step signup form, handles URL invite code
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { StudentSignupData, StudentUser, InviteCodeValidation } from '../service/types'
import { validateInviteCode } from '../service/studentSignup'

/** Initial signup data */
const initialData: StudentSignupData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  inviteCode: '',
  googleToken: undefined,
}

/** Context value interface */
interface StudentSignupContextType {
  /** Current signup data */
  data: StudentSignupData
  /** Update signup data */
  setData: (updates: Partial<StudentSignupData>) => void
  /** Whether user authenticated with Google */
  isGoogleAuth: boolean
  /** Set Google auth data (email, name, token) */
  setGoogleAuth: (token: string, email: string, firstName: string, lastName: string) => void
  /** Created user data from signup API */
  user: StudentUser | null
  /** Set created user data */
  setUser: (user: StudentUser) => void
  /** Reset entire context */
  reset: () => void
  /** Invite code from URL (readonly) */
  urlCode: string | null
  /** Invite code validation result */
  codeValidation: InviteCodeValidation | null
  /** Whether code is being validated */
  validatingCode: boolean
  /** Validate invite code */
  validateCode: (code: string) => Promise<InviteCodeValidation>
}

const StudentSignupContext = createContext<StudentSignupContextType | undefined>(undefined)

/**
 * Provider for student signup flow state
 * @param children - Child components
 */
export const StudentSignupProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams()
  const urlCode = searchParams.get('code')

  const [data, setDataState] = useState<StudentSignupData>(() => ({
    ...initialData,
    inviteCode: urlCode?.toUpperCase() || '',
  }))
  const [user, setUserState] = useState<StudentUser | null>(null)
  const [codeValidation, setCodeValidation] = useState<InviteCodeValidation | null>(null)
  const [validatingCode, setValidatingCode] = useState(false)

  const setData = useCallback((updates: Partial<StudentSignupData>) => {
    setDataState((prev) => ({ ...prev, ...updates }))
  }, [])

  const setGoogleAuth = useCallback((token: string, email: string, firstName: string, lastName: string) => {
    setDataState((prev) => ({
      ...prev,
      googleToken: token,
      email,
      firstName,
      lastName,
    }))
  }, [])

  const setUser = useCallback((userData: StudentUser) => {
    setUserState(userData)
  }, [])

  const reset = useCallback(() => {
    setDataState({
      ...initialData,
      inviteCode: urlCode?.toUpperCase() || '',
    })
    setUserState(null)
    setCodeValidation(null)
  }, [urlCode])

  const validateCode = useCallback(async (code: string): Promise<InviteCodeValidation> => {
    if (!code.trim()) {
      const result: InviteCodeValidation = { valid: false, error: 'Code is required' }
      setCodeValidation(result)
      return result
    }

    setValidatingCode(true)
    try {
      const result = await validateInviteCode(code)
      setCodeValidation(result)
      return result
    } catch (error: unknown) {
      const result: InviteCodeValidation = {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation failed',
      }
      setCodeValidation(result)
      return result
    } finally {
      setValidatingCode(false)
    }
  }, [])

  const isGoogleAuth = Boolean(data.googleToken)

  // Auto-validate code from URL on mount
  useEffect(() => {
    if (urlCode) {
      validateCode(urlCode)
    }
  }, [urlCode, validateCode])

  const value = useMemo(
    () => ({
      data,
      setData,
      isGoogleAuth,
      setGoogleAuth,
      user,
      setUser,
      reset,
      urlCode,
      codeValidation,
      validatingCode,
      validateCode,
    }),
    [
      data,
      setData,
      isGoogleAuth,
      setGoogleAuth,
      user,
      setUser,
      reset,
      urlCode,
      codeValidation,
      validatingCode,
      validateCode,
    ]
  )

  return <StudentSignupContext.Provider value={value}>{children}</StudentSignupContext.Provider>
}

/**
 * Hook to access student signup context
 * @throws Error if used outside StudentSignupProvider
 */
export const useStudentSignupContext = (): StudentSignupContextType => {
  const context = useContext(StudentSignupContext)
  if (!context) {
    throw new Error('useStudentSignupContext must be used within StudentSignupProvider')
  }
  return context
}
